import React, { useState, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    Button,
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import { createUserAPI, editUserAPI } from "Samarth Module/api/userAPI";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";
import { addUserInAdGroup } from "Samarth Module/api/azureADGroupAPI";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import { getAdminDetails } from "Samarth Module/api/roleBaseApi";

// Constants
const INITIAL_FORM_STATE = {
    name: '',
    email: '',
    userType: '',
    accessType: 'Read-Write',
    persona: '',
    personaAccess: [],
};

const USER_TYPES = [
    { value: 'admin', label: 'Admin' },
    { value: 'member', label: 'Member' },
    { value: 'super admin', label: 'Super Admin' }
];

const ACCESS_TYPES = [
    { value: 'Read', label: 'Read' },
    { value: 'Read-Write', label: 'Read-Write' }
];

const ALL_ACCESS_KEY = "All Access";

const normalizeScopeValuesForForm = (scopeValue) => {
    if (Array.isArray(scopeValue)) {
        return scopeValue
            .map(value => (value === null || value === undefined ? '' : String(value).trim()))
            .filter(Boolean);
    }

    if (scopeValue === null || scopeValue === undefined || scopeValue === '') {
        return [];
    }

    return String(scopeValue)
        .split(',')
        .map(value => value.replaceAll('[', '').replaceAll(']', '').replaceAll("'", '').replaceAll('"', '').replaceAll('`', '').trim())
        .filter(Boolean);
};

const AddAndEditPopup = ({ type, selectedUserData, open, onClose, onSubmit, personaMapping = {}, dropdownData = {}, showSnackbar, activeAccount, instance }) => {
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [formError, setFormError] = useState('');
    const [snackBarObject, setSnackBarObject] = useState({
        type: 'success',
        message: '',
        snackbarOpen: false
    });

    const showLocalSnackbar = useCallback((type, message) => {
        if (showSnackbar) {
            showSnackbar(type, message);
        } else {
            setSnackBarObject({
                type,
                message,
                snackbarOpen: true
            });
        }
    }, [showSnackbar]);

    const closeLocalSnackbar = useCallback(() => {
        setSnackBarObject(prev => ({
            ...prev,
            snackbarOpen: false
        }));
    }, []);

    const personaOptions = useMemo(() => Object.keys(personaMapping), [personaMapping]);

    const selectedPersonaAccess = useMemo(() => {
        if (!formData.persona || !personaMapping[formData.persona]) return [];
        return personaMapping[formData.persona];
    }, [formData.persona, personaMapping]);

    const isAllAccess = useMemo(() => {
        return selectedPersonaAccess[0] === ALL_ACCESS_KEY;
    }, [selectedPersonaAccess]);

    const accessDropdownOptions = useMemo(() => {
        const accessType = selectedPersonaAccess[0];
        const options = dropdownData[accessType] || [];
        return options;
    }, [selectedPersonaAccess, dropdownData]);

    useEffect(() => {
        if (type === 'edit' && selectedUserData && open) {
            setFormData({
                name: selectedUserData.Name || '',
                email: selectedUserData['Email ID'] || '',
                userType: selectedUserData['User Type'] || '',
                accessType: selectedUserData['Access Type'] || '',
                persona: selectedUserData.Persona || '',
                personaAccess: normalizeScopeValuesForForm(selectedUserData['Scope Value'])
            });
        } else if (type === 'add' && open) {
            setFormData(INITIAL_FORM_STATE);
        }
        if (open) {
            setFormError('');
        }
    }, [type, selectedUserData, open]);

    const handleClose = useCallback(() => {
        setFormData(INITIAL_FORM_STATE);
        setFormError('');
        onClose?.();
    }, [onClose]);

    const handleInputChange = useCallback((event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'persona' ? { personaAccess: [] } : {})
        }));
    }, []);

    const handlePersonaAccessChange = useCallback((label, values) => {
        setFormData(prev => ({
            ...prev,
            personaAccess: values
        }));
    }, []);

    const validateForm = useCallback(() => {
        const { name, email, userType, persona, personaAccess } = formData;

        if (!name?.trim() || !email?.trim() || !userType || !persona) {
            return 'Please fill in all fields';
        }

        if (!isAllAccess && (!personaAccess || personaAccess.length === 0)) {
            return 'Please select access level';
        }

        return null;
    }, [formData, isAllAccess]);

    const buildSubmitData = useCallback(() => ({
        name: formData.name,
        email: formData.email,
        userType: formData.userType,
        accessType: formData.accessType,
        persona: formData.persona,
        scope: isAllAccess ? "All Access" : selectedPersonaAccess[0],
        scope_value: isAllAccess ? [] : formData.personaAccess,
        type: type
    }), [formData, isAllAccess, selectedPersonaAccess, type]);

    const buildNewUserData = useCallback((response) => ({
        ID: type === 'edit' ? selectedUserData.ID : response.data?.user_id,
        Name: response.data?.name || formData.name,
        "Email ID": response.data?.email || formData.email,
        Persona: response.data?.persona || formData.persona,
        "User Type": formData.userType,
        "Access Type": formData.accessType,
        Scope: response.data?.scope || (isAllAccess ? "All Access" : selectedPersonaAccess[0]),
        "Scope Value": response.data?.scope_value || (isAllAccess ? [] : formData.personaAccess)
    }), [type, selectedUserData, formData, isAllAccess, selectedPersonaAccess]);

    const getErrorMessage = useCallback((error) => {
        if (error?.message?.includes('Failed to add user to AD Group')) {
            return 'Failed to add user to Active Directory group. Please check user permissions and try again.';
        }
        return error?.response?.data?.message ||
            error?.message ||
            `Failed to ${type === 'edit' ? 'update' : 'create'} user. Please try again.`;
    }, [type]);

    const handleEditUser = useCallback(async (submitData) => {
        return await editUserAPI(submitData, selectedUserData.ID);
    }, [selectedUserData]);

    const handleCreateUser = useCallback(async (submitData) => {
        const adGroupStatus = await addUserInAdGroup(formData.email, instance, activeAccount);
        if (adGroupStatus !== 204 && adGroupStatus !== 200) {
            throw new Error(`Failed to add user to AD Group. Status: ${adGroupStatus}`);
        }
        return await createUserAPI(submitData);
    }, [formData.email, instance, activeAccount]);

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();
        setFormError('');

        const validationError = validateForm();
        if (validationError) {
            setFormError(validationError);
            showLocalSnackbar('error', validationError);
            return;
        }

        const submitData = buildSubmitData();

        try {
            const response = type === 'edit'
                ? await handleEditUser(submitData)
                : await handleCreateUser(submitData);

            const adminResponse = await getAdminDetails();

            if (adminResponse?.data) {
                localStorage.setItem(
                    "UserInfo",
                    JSON.stringify(adminResponse.data)
                );
            }

            const newUserData = buildNewUserData(response);
            const successMessage = response?.message || `User ${type === 'edit' ? 'updated' : 'created'} successfully!`;

            setFormData(INITIAL_FORM_STATE);
            onSubmit?.(newUserData, successMessage);
            handleClose();
        } catch (error) {
            console.error(`Error ${type === 'edit' ? 'updating' : 'creating'} user:`, error);
            const errorMessage = getErrorMessage(error);
            setFormError(errorMessage);
            showLocalSnackbar('error', errorMessage);
        }
    }, [validateForm, showLocalSnackbar, buildSubmitData, type, handleEditUser, handleCreateUser, buildNewUserData, onSubmit, handleClose, getErrorMessage]);

    const renderFormField = useCallback((label, children) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500, mb: 0.2, fontSize: '0.875rem' }}>
                {label}
            </Typography>
            {children}
        </Box>
    ), []);

    // Common dropdown styles to maintain consistency
    const dropdownStyles = {
        select: {
            '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                minHeight: 'auto',
                padding: '6px 12px',
                fontSize: '0.875rem'
            }
        },
        menuProps: {
            PaperProps: {
                sx: {
                    maxHeight: 180,
                    '& .MuiMenuItem-root': {
                        fontSize: '13px',
                        py: 0.5
                    }
                }
            }
        }
    };

    const renderAccessField = useCallback(() => {
        if (!formData.persona) return null;
        const accessType = selectedPersonaAccess[0];
        
        return (
            <Grid size={{ xs: 12, sm: 6 }}>
                {isAllAccess ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500, mb: 0.2, fontSize: '0.875rem' }}>
                            {accessType}
                        </Typography>
                        <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                You will have all Site Cluster and BG level access
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <MultiSelectWithSearchInput
                        label={accessType}
                        options={accessDropdownOptions}
                        filterValues={formData.personaAccess}
                        handleFilter={handlePersonaAccessChange}
                        disabled={false}
                        isLabelShow={true}
                        type="edit user"
                        isMultiSelect={true}
                    />
                )}
            </Grid>
        );
    }, [formData.persona, formData.personaAccess, selectedPersonaAccess, isAllAccess, accessDropdownOptions, handlePersonaAccessChange]);

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: 1 } } }}>
                <DialogTitle sx={{ m: 0, p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#2036C7', color: '#FFFFFF', minHeight: 'auto' }}>
                    <Typography variant="h6" component="div" sx={{ fontSize: '1rem' }}>
                        {type === "add" ? "Add User" : "Edit User"}
                    </Typography>
                    <IconButton aria-label="close" onClick={handleClose} sx={{ color: '#FFFFFF', p: 0.5 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 2, '& .MuiDialogTitle-root': { paddingTop: '10px' }, '&.MuiDialogContent-dividers': { borderTop: 'none', borderBottom: 'none' } }}>
                    {formError && (
                        <Alert severity="error" onClose={() => setFormError('')} sx={{ mb: 1 }}>
                            {formError}
                        </Alert>
                    )}
                    <Box component="form" onSubmit={handleSubmit}>
                        <Grid container spacing={2} sx={{ p: 2 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                {renderFormField(
                                    "Name",
                                    <TextField
                                        fullWidth
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        required
                                        placeholder="Enter name"
                                        size="small"
                                        disabled={type === 'edit'}
                                        sx={{ '& .MuiInputBase-input': { py: 1, fontSize: '0.875rem' } }}
                                    />
                                )}
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                {renderFormField(
                                    "Email",
                                    <TextField
                                        fullWidth
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        required
                                        placeholder="Enter email"
                                        size="small"
                                        disabled={type === 'edit'}
                                        sx={{
                                            '& .MuiInputBase-input': { py: 1, fontSize: '0.875rem' },
                                            ...(type === 'edit' && { '& .MuiInputBase-input': { color: 'text.disabled', backgroundColor: '#f5f5f5', py: 1 } })
                                        }}
                                    />
                                )}
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                {renderFormField(
                                    "User Type",
                                    <FormControl fullWidth variant="outlined" required size="small">
                                        <Select
                                            name="userType"
                                            value={formData.userType}
                                            onChange={handleInputChange}
                                            displayEmpty
                                            renderValue={(selected) => {
                                                if (!selected || selected === '') {
                                                    return <em style={{ color: '#999', fontSize: '0.875rem' }}>Select User Type</em>;
                                                }
                                                const selectedOption = USER_TYPES.find(option => option.value === selected);
                                                return selectedOption ? selectedOption.label : <em style={{ color: '#999', fontSize: '0.875rem' }}>Select User Type</em>;
                                            }}
                                            sx={dropdownStyles.select}
                                            MenuProps={dropdownStyles.menuProps}
                                        >
                                            <MenuItem value="" disabled>
                                                <em style={{ fontSize: '0.875rem' }}>Select User Type</em>
                                            </MenuItem>
                                            {USER_TYPES.map(({ value, label }) => (
                                                <MenuItem key={value} value={value} sx={{ fontSize: '13px', color: '#333', py: 0.5 }}>
                                                    {label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                {renderFormField(
                                    "Access Type",
                                    <FormControl fullWidth variant="outlined" required size="small">
                                        <Select
                                            name="accessType"
                                            value={formData.accessType}
                                            onChange={handleInputChange}
                                            displayEmpty
                                            renderValue={(selected) => {
                                                if (!selected || selected === '') {
                                                    return <em style={{ color: '#999', fontSize: '0.875rem' }}>Select Access Type</em>;
                                                }
                                                const selectedOption = ACCESS_TYPES.find(option => option.value === selected);
                                                return selectedOption ? selectedOption.label : <em style={{ color: '#999', fontSize: '0.875rem' }}>Select Access Type</em>;
                                            }}
                                            sx={dropdownStyles.select}
                                            MenuProps={dropdownStyles.menuProps}
                                        >
                                            <MenuItem value="" disabled>
                                                <em style={{ fontSize: '0.875rem' }}>Select Access Type</em>
                                            </MenuItem>
                                            {ACCESS_TYPES.map(({ value, label }) => (
                                                <MenuItem key={value} value={value} sx={{ fontSize: '13px', color: '#333', py: 0.5 }}>
                                                    {label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                {renderFormField(
                                    "Persona",
                                    <FormControl fullWidth variant="outlined" required size="small">
                                        <Select
                                            name="persona"
                                            value={formData.persona}
                                            onChange={handleInputChange}
                                            displayEmpty
                                            renderValue={(selected) => {
                                                if (!selected || selected === '') {
                                                    return <em style={{ color: '#999', fontSize: '0.875rem' }}>Select Persona</em>;
                                                }
                                                return selected;
                                            }}
                                            sx={dropdownStyles.select}
                                            MenuProps={dropdownStyles.menuProps}
                                        >
                                            <MenuItem value="" disabled>
                                                <em style={{ fontSize: '0.875rem' }}>Select Persona</em>
                                            </MenuItem>
                                            {personaOptions.map((persona) => (
                                                <MenuItem key={persona} value={persona} sx={{ fontSize: '13px', color: '#333', py: 0.5 }}>
                                                    {persona}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Grid>

                            {renderAccessField()}
                        </Grid>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
                    <Button variant="outlined" onClick={handleClose} sx={{ minWidth: 80, py: 0.5, fontSize: '0.875rem' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{ minWidth: 80, py: 0.5, fontSize: '0.875rem', backgroundColor: '#2036C7', '&:hover': { backgroundColor: '#1a2ba6' } }}
                    >
                        {type === "add" ? "Add" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {!showSnackbar && (
                <SnackBarMessage
                    type={snackBarObject.type}
                    snackbarMessage={snackBarObject.message}
                    snackbarOpen={snackBarObject.snackbarOpen}
                    setSnackbarOpen={closeLocalSnackbar}
                />
            )}
        </>
    );
};

export default AddAndEditPopup;

AddAndEditPopup.propTypes = {
    type: PropTypes.oneOf(['add', 'edit']),
    selectedUserData: PropTypes.object,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    personaMapping: PropTypes.object,
    dropdownData: PropTypes.object,
    showSnackbar: PropTypes.func,
    activeAccount: PropTypes.object,
    instance: PropTypes.object,
};